import type {
  AIResponse,
  DeliveryRecord,
  Order,
  PricingRow,
  Site,
  SourcingRow,
  Team,
  TeamSettings,
  Yard,
  YardMaterialPrice,
} from '../types';

const API_BASE = '/api';
let authToken: string | null = null;
export function setAuthToken(t: string | null) {
  authToken = t;
}

export class ApiError extends Error {
  constructor(msg: string, public status: number, public details: any[] = []) {
    super(msg);
  }
}

async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs = 60000
): Promise<T> {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (authToken) (headers as Record<string, string>)['Authorization'] = `Bearer ${authToken}`;
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      signal: controller.signal,
    });
    clearTimeout(id);
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: 'Request failed' }));
      throw new ApiError(body.error || `HTTP ${res.status}`, res.status, body.details ?? []);
    }
    return res.json();
  } catch (err: any) {
    clearTimeout(id);
    if (err.name === 'AbortError') throw new ApiError('Request timed out.', 408);
    throw err;
  }
}

// ---- email OTP login (unauthenticated) ----
export const otpSend = (email: string) =>
  fetchWithAuth<{ ok: boolean }>('/auth/otp/send', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

export const otpVerify = (email: string, token: string) =>
  fetchWithAuth<{ session: { access_token: string; refresh_token: string } }>(
    '/auth/otp/verify',
    { method: 'POST', body: JSON.stringify({ email, token }) }
  );

// ---- me / settings ----
export const getMe = () =>
  fetchWithAuth<{ user: any; team: Team; settings: TeamSettings | null }>('/me');

export const updateSettings = (patch: Partial<TeamSettings>) =>
  fetchWithAuth<{ settings: TeamSettings }>('/settings', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });

// ---- yards ----
export const getAllYards = () => fetchWithAuth<{ yards: Yard[] }>('/yards/all');
export const updateYards = (yards: Partial<Yard>[]) =>
  fetchWithAuth<{ ok: boolean; count: number }>('/yards', {
    method: 'PUT',
    body: JSON.stringify({ yards }),
  });
export const deleteYard = (yardId: string) =>
  fetchWithAuth<{ ok: boolean }>(`/yards/${encodeURIComponent(yardId)}`, { method: 'DELETE' });

// ---- sites ----
export const getAllSites = () => fetchWithAuth<{ sites: Site[] }>('/sites/all');
export const updateSites = (sites: Partial<Site>[]) =>
  fetchWithAuth<{ ok: boolean; count: number }>('/sites', {
    method: 'PUT',
    body: JSON.stringify({ sites }),
  });
export const deleteSite = (siteId: string) =>
  fetchWithAuth<{ ok: boolean }>(`/sites/${encodeURIComponent(siteId)}`, { method: 'DELETE' });

// ---- prices ----
export const getAllPrices = () =>
  fetchWithAuth<{ prices: YardMaterialPrice[] }>('/prices/all');
export const updatePrices = (
  prices: Array<{ yard_id: string; material: string; price: number | null }>
) =>
  fetchWithAuth<{ ok: boolean; count: number }>('/prices', {
    method: 'PUT',
    body: JSON.stringify({ prices }),
  });

// ---- pricing / sourcing ----
export const getPricing = (yardId: string, material: string) =>
  fetchWithAuth<{ rows: PricingRow[] }>(
    `/pricing?yard_id=${encodeURIComponent(yardId)}&material=${encodeURIComponent(material)}`
  );
export const getSourcing = (siteId: string, material: string) =>
  fetchWithAuth<{ rows: SourcingRow[] }>(
    `/sourcing?site_id=${encodeURIComponent(siteId)}&material=${encodeURIComponent(material)}`
  );

// ---- delivery records ----
export const importDeliveryRecords = (records: any[]) =>
  fetchWithAuth<{ ok: boolean; inserted: number; curve_n_points: number }>(
    '/delivery-records/import',
    { method: 'POST', body: JSON.stringify({ records }) },
    120000
  );
export const getDeliveryRecords = (limit = 500, offset = 0) =>
  fetchWithAuth<{ records: DeliveryRecord[] }>(
    `/delivery-records?limit=${limit}&offset=${offset}`
  );

// ---- orders ----
export const createOrder = (order: {
  yard_id: string;
  site_id?: string | null;
  site_address: string;
  material: string;
  quantity: number;
  delivered_price_per_unit?: number | null;
  status?: string;
}) => fetchWithAuth<{ order: Order }>('/orders', { method: 'POST', body: JSON.stringify(order) });

export const getOrders = (limit = 100) =>
  fetchWithAuth<{ orders: Order[] }>(`/orders?limit=${limit}`);

export const updateOrderStatus = (id: string, status: string) =>
  fetchWithAuth<{ order: Order }>(`/orders/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });

export const resolveCheapest = (site_address: string, material: string) =>
  fetchWithAuth<{ resolved: any }>('/orders/resolve', {
    method: 'POST',
    body: JSON.stringify({ site_address, material }),
  });

// ---- AI ----
export const sendAICommand = (messages: Array<{ role: string; content: string }>) =>
  fetchWithAuth<AIResponse>('/ai-command/plan', {
    method: 'POST',
    body: JSON.stringify({ messages }),
  });

// ---- geocode ----
export const geocode = (address: string) =>
  fetchWithAuth<{ lat: number; lon: number; matched_address: string }>(
    `/geocode?address=${encodeURIComponent(address)}`
  );
